import React, { useState } from 'react';
import styled from 'styled-components';
import { formatSaldo } from '../../utils/formatters';
import { formatHoraForDisplay } from '../../utils/dateHelpers';
import { formatIsoDay, totalesDelBucket } from '../../utils/acreditacionesEsperadas';

const Aviso = styled.div`
  background-color: rgba(26, 40, 62, 0.05);
  border: 1px solid rgba(26, 40, 62, 0.18);
  border-left: 4px solid ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius};
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.secondary};
`;

const Fila = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
`;

const Titulo = styled.strong`
  font-family: ${props => props.theme.typography.titleFamily};
  color: ${props => props.theme.colors.primary};
`;

const Dato = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  strong { color: ${props => props.theme.colors.secondary}; }
`;

const PendientesBadge = styled.span`
  background-color: ${props => (props.$cero ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.18)')};
  color: ${props => (props.$cero ? '#065F46' : '#92400E')};
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  &:disabled { color: ${props => props.theme.colors.textSecondary}; cursor: wait; }
`;

const Nota = styled.p`
  margin: 0.4rem 0 0 0;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ListaSinFila = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.2rem;
  li { margin: 0.2rem 0; }
`;

/**
 * Aviso de Control de Saldos con las acreditaciones esperadas del día para la
 * moneda activa. Si no hay cálculo del SGC para hoy lo dice explícitamente: un
 * "0 eventos" solo se muestra cuando el cálculo corrió.
 *
 * `sinFila`: comitentes con eventos que no aparecen en la tabla de la moneda
 * (sin saldo en el custodio); se listan para no perderlos.
 */
const AcreditacionesEsperadasAviso = ({
  moneda, meta, isLoading, error, resumen, sinFila = [], puedeRecalcular, onRecalcular, onVerComitente,
}) => {
  const [recalculando, setRecalculando] = useState(false);
  const [mostrarSinFila, setMostrarSinFila] = useState(false);

  const handleRecalcular = async () => {
    setRecalculando(true);
    try {
      await onRecalcular();
    } finally {
      setRecalculando(false);
    }
  };

  const botonRecalcular = puedeRecalcular && (
    <LinkButton type="button" onClick={handleRecalcular} disabled={recalculando}>
      {recalculando ? 'Actualizando…' : 'Actualizar'}
    </LinkButton>
  );

  if (isLoading) return null;

  if (error || !meta || meta.error) {
    return (
      <Aviso role="status">
        <Fila>
          <Titulo>Acreditaciones esperadas hoy</Titulo>
          <Dato>
            {error ? 'No se pudieron leer.' : 'Sin datos del SGC para hoy.'}
            {meta?.error ? ` ${meta.error}` : ''}
          </Dato>
          {botonRecalcular}
        </Fila>
      </Aviso>
    );
  }

  const { eventos, pendientes } = totalesDelBucket(resumen);

  return (
    <Aviso role="status">
      <Fila>
        <Titulo>Acreditaciones esperadas hoy</Titulo>
        <Dato><strong>{eventos}</strong> {eventos === 1 ? 'evento' : 'eventos'}</Dato>
        {eventos > 0 && (
          <PendientesBadge $cero={pendientes === 0}>
            {pendientes === 0 ? 'Todas corroboradas' : `${pendientes} sin corroborar`}
          </PendientesBadge>
        )}
        <Dato>Cartera SGC al <strong>{formatIsoDay(meta.fechaCartera)}</strong></Dato>
        {meta.generatedAt && <Dato>calculado {formatHoraForDisplay(meta.generatedAt)}</Dato>}
        {botonRecalcular}
      </Fila>
      {sinFila.length > 0 && (
        <>
          <Nota>
            {sinFila.length} {sinFila.length === 1 ? 'comitente tiene' : 'comitentes tienen'} eventos
            en {moneda === 'ARS' ? 'pesos' : 'esta moneda'} pero no figura en la tabla.{' '}
            <LinkButton type="button" onClick={() => setMostrarSinFila(v => !v)}>
              {mostrarSinFila ? 'Ocultar' : 'Ver'}
            </LinkButton>
          </Nota>
          {mostrarSinFila && (
            <ListaSinFila>
              {sinFila.map(r => (
                <li key={r.nroComitente}>
                  <LinkButton type="button" onClick={() => onVerComitente(r)}>
                    {r.nroComitente} — {r.nombre}
                  </LinkButton>{' '}
                  {formatSaldo(r.total, moneda)}
                </li>
              ))}
            </ListaSinFila>
          )}
        </>
      )}
      <Nota>
        Cupones, amortizaciones, vencimientos de letras y T-Bills, y cheques/pagarés que vencen hoy según la
        cartera del SGC. Se suman al saldo recién cuando los marcás Acreditada en el desglose: la liquidación puede
        acreditarlos otro día. En las letras sin cronograma del proveedor (≥) se muestra solo el capital.
      </Nota>
    </Aviso>
  );
};

export default AcreditacionesEsperadasAviso;
